import { ScrollView, Text, View } from 'react-native'
import { useQuery } from '@apollo/client'
import { useLocalSearchParams } from 'expo-router'
import { useTranslation } from 'react-i18next'
import { useSafeAreaInsets } from 'react-native-safe-area-context'

import { GET_JBP_DEPOSIT_INVOICE } from '@/constants/graphql/queries'
import { formatTokenAmount } from '@/config/marketPresentation'
import { convertToDate } from '@/utils/formatter'
import { formatJbpCurrency, getJbpPaymentMethodLabel, type JbpDepositInvoice, type JbpPaymentMethodKind } from '@/utils/jbp'
import DescriptionListItem from '@/components/DescriptionListItem'
import LoadingScreen from '@/components/LoadingScreen'
import NotFoundScreen from '@/components/NotFoundScreen'
import SectionHeader from '@/components/SectionHeader'

type JbpDepositInvoiceQuery = {
  jbpDepositInvoice: JbpDepositInvoice
}

export default function JbpDepositInvoiceDetailsScreen() {
  const { bottom } = useSafeAreaInsets()
  const { t } = useTranslation()
  const { id } = useLocalSearchParams<{ id: string }>()
  const { data, loading } = useQuery<JbpDepositInvoiceQuery>(GET_JBP_DEPOSIT_INVOICE, {
    variables: { invoiceId: id },
    skip: !id
  })

  if (loading) {
    return <LoadingScreen />
  }

  const invoice = data?.jbpDepositInvoice
  if (!invoice) {
    return <NotFoundScreen />
  }
  const finishedAt = invoice.settledAt ?? invoice.closedAt
  const isSettled = ['COMPLETED', 'MANUALLY_COMPLETED', 'ADMIN_RESOLVED'].includes(invoice.state)
  const expectedCredit = Math.max(
    0,
    Number(invoice.requireAmount ?? invoice.requestedAmount) - Number(invoice.transactionFeeAmount ?? 0)
  )

  return (
    <ScrollView className={'bg-background-secondary'} contentContainerStyle={{ paddingBottom: bottom }}>
      <View className={'h-[52px] border-b border-border-10 bg-background-primary px-4 pt-2'}>
        <Text className={'text-2xl font-semibold text-text-primary'}>{t('c2c:fastPayDetails')}</Text>
      </View>

      <SectionHeader>{t('orders:orderInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:systemOrderNumber')} canCopy={true} hasBorderTop={true}>
        {invoice.companyOrderNum}
      </DescriptionListItem>
      {invoice.mownecumOrderNum && (
        <DescriptionListItem label={t('c2c:providerOrderNumber')} canCopy={true}>
          {invoice.mownecumOrderNum}
        </DescriptionListItem>
      )}
      {invoice.walletCreditId && (
        <DescriptionListItem label={t('wallet:walletTransactionNumber')} canCopy={true}>
          {invoice.walletCreditId}
        </DescriptionListItem>
      )}
      <DescriptionListItem label={t('orders:paymentMethod')}>{getPaymentMethodLabel(invoice.bankId)}</DescriptionListItem>
      <DescriptionListItem label={t('c2c:orderStatus')}>
        {t(`orderList:fastTrack.${getInvoiceStateKey(invoice.state)}`)}
      </DescriptionListItem>
      <DescriptionListItem label={t('orders:createdAt')}>{convertToDate(new Date(invoice.createdAt).valueOf())}</DescriptionListItem>
      {finishedAt && (
        <DescriptionListItem label={t(invoice.settledAt ? 'orders:completedAt' : 'c2c:closedAt')} hasBorderBottom={true}>
          {convertToDate(new Date(finishedAt).valueOf())}
        </DescriptionListItem>
      )}
      {!finishedAt && <View className={'h-px bg-border-10'} />}

      <SectionHeader>{t('c2c:amountInformation')}</SectionHeader>
      <DescriptionListItem label={t('orders:requestedAmount')} hasBorderTop={true}>
        {formatJbpCurrency(invoice.requestedAmount)}
      </DescriptionListItem>
      <DescriptionListItem label={t('c2c:amountDue')}>
        {formatJbpCurrency(invoice.requireAmount ?? invoice.requestedAmount)}
      </DescriptionListItem>
      {Number(invoice.paidAmount) > 0 && (
        <DescriptionListItem label={t('orders:actualPaidAmount')}>
          {formatJbpCurrency(invoice.paidAmount)}
        </DescriptionListItem>
      )}
      <DescriptionListItem label={t('c2c:fee')}>{formatJbpCurrency(invoice.transactionFeeAmount)}</DescriptionListItem>
      <DescriptionListItem label={t(isSettled ? 'c2c:creditedQuantity' : 'c2c:expectedCredit')} hasBorderBottom={true}>
        {formatTokenAmount(isSettled ? invoice.creditedAmount : expectedCredit.toString())}
      </DescriptionListItem>
    </ScrollView>
  )
}

function getPaymentMethodLabel(bankId: string) {
  const method: JbpPaymentMethodKind | undefined =
    bankId === '1' || bankId === '51' ? 'BANK_CARD' : bankId === '30' ? 'ALIPAY' : bankId === '40' ? 'WECHAT' : undefined
  return method ? getJbpPaymentMethodLabel(method) : '−'
}

function getInvoiceStateKey(state: string) {
  switch (state) {
    case 'CREATING':
      return 'creating'
    case 'AWAIT_SETTLEMENT':
      return 'awaitSettlement'
    case 'ADMIN_RESOLVING':
      return 'adminResolving'
    case 'COMPLETED':
      return 'completed'
    case 'MANUALLY_COMPLETED':
    case 'ADMIN_RESOLVED':
      return 'completed'
    case 'FAILED':
      return 'failed'
    case 'TIMED_OUT':
      return 'timedOut'
    default:
      return 'all'
  }
}
